(() => {
  if (window.NexPreorderPerks) return;
  const surfaces = () => [...document.querySelectorAll('nex-hero-purchase, nex-gdab')];
  const selected = surface => surface.querySelector('.hero-purchase__color[aria-checked="true"], .gdab__card[aria-checked="true"]');
  const dataKeys = ['dockReady', 'dockAvailable', 'dockTracked', 'giftReady', 'giftRemaining', 'giftPool', 'giftPercent'];
  const number = value => Number.isFinite(Number(value)) ? Number(value) : 0;
  const stateFor = ({ available, preorder, future, dockReady, dockAvailable, giftReady, remaining }) => {
    if (!available || !dockReady) return 'hidden';
    const canSupplyDock = dockAvailable === undefined ? remaining > 0 : dockAvailable;
    if (preorder ?? future) return giftReady ? (canSupplyDock ? 'gift' : 'exhausted') : 'no-gift';
    return canSupplyDock ? 'retail' : 'unavailable';
  };
  const setData = (node, key, value) => {
    if (node && node.dataset[key] !== String(value)) node.dataset[key] = String(value);
  };
  const copyData = (from, to) => {
    if (!from || !to) return;
    dataKeys.forEach(key => { if (key in from.dataset) to.dataset[key] = from.dataset[key]; });
  };
  const revealProgress = node => {
    if (node.hidden) return;
    const drawer = node.closest('nex-gdab');
    if (drawer && drawer.dataset.gdabState !== 'panel') return;
    node.dataset.giftRevealed = 'true';
  };
  const progressObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => { if (entry.isIntersecting) revealProgress(entry.target); });
  }, { threshold: .25 }) : null;
  const renderProgress = (module, state) => {
    const progress = module.querySelector('[data-gift-progress]');
    if (!progress) return;
    copyData(module, progress);
    const percent = Math.max(0, Math.min(100, Math.floor(number(module.dataset.giftPercent))));
    progress.hidden = (state !== 'gift' && state !== 'retail') || percent < 50 || number(module.dataset.giftPool) <= 0 || module.dataset.dockTracked === 'false';
    const track = progress.querySelector('[role="progressbar"]');
    track?.setAttribute('aria-valuenow', String(percent));
    if (track?.dataset.giftProgressDescription) track.setAttribute('aria-label', track.dataset.giftProgressDescription.replace('__count__', String(number(module.dataset.giftPool))));
    progress.querySelector('.nex-gift-progress__fill')?.style.setProperty('--gift-progress', percent + '%');
    const label = progress.querySelector('[data-gift-progress-label]');
    if (label) {
      // Gift reservations and paid add-ons share inventory, with copy for the selected offer.
      const template = (state === 'retail' ? label.dataset.giftLabelSold : label.dataset.giftLabelClaimed) || label.dataset.giftLabelTemplate || label.textContent.replace(/[\d.,]+(?=\s*%)/, '{percent}');
      label.dataset.giftLabelTemplate = template;
      label.textContent = template.replace('{percent}', String(percent)).replace('__percent__', String(percent));
    }
    if (!progress.hidden && module.closest('nex-gdab')?.dataset.gdabState === 'panel') revealProgress(progress);
  };
  const sync = (surface, card = selected(surface)) => {
    if (!card) return;
    // This hook only exists after an explicit draft-only design-review URL.
    window.NexPurchaseReview?.apply(surface, card);
    const available = card.dataset.inventoryAvailable === 'true';
    const preorder = card.dataset.preorder === 'true';
    const future = available && card.dataset.preorderFuture === 'true';
    surface.dataset.preorderFuture = String(future);
    const promiseMode = available && preorder ? 'preorder' : 'stock';
    surface.querySelectorAll('[data-purchase-promise]').forEach(promise => {
      for (const part of ['Heading', 'Detail']) {
        const node = promise.querySelector('[data-promise-' + part.toLowerCase() + ']');
        const value = promise.dataset[promiseMode + part];
        if (node && value !== undefined && node.textContent !== value) node.textContent = value;
      }
    });
    surface.querySelectorAll('[data-preorder-details]').forEach(module => {
      const state = stateFor({
        available, preorder,
        dockReady: module.dataset.dockReady === 'true',
        dockAvailable: module.dataset.dockAvailable === 'true',
        giftReady: module.dataset.giftReady === 'true',
        remaining: number(module.dataset.giftRemaining)
      });
      setData(module, 'dockState', state);
      module.hidden = state === 'hidden';
      setData(surface, 'dockState', state);
      module.querySelectorAll('[data-dock-copy]').forEach(node => { node.hidden = node.dataset.dockCopy !== state; });
      module.querySelectorAll('[data-dock-price]').forEach(node => {
        node.hidden = node.dataset.dockPrice === 'gift' ? state !== 'gift' : state !== 'retail' && state !== 'unavailable';
      });
      renderProgress(module, state);
    });
    if (available && surface.dataset.checkoutGiftLabel) {
      const comparison = surface.querySelector('nex-price-compare');
      const label = surface.dataset.dockState === 'gift'
        ? comparison?.dataset.saveLabel || surface.dataset.checkoutGiftLabel
        : preorder ? comparison?.dataset.bagSaveLabel || surface.dataset.checkoutPreorderLabel : surface.dataset.checkoutStockLabel;
      if (label) {
        setData(card, 'checkoutLabel', label);
        setData(card, 'chipLabel', label);
        if (surface.selectedVariant) surface.selectedVariant.checkoutLabel = label;
        if (!surface._checkoutAttempt && !window.NexDock?.busy) {
          const button = surface.querySelector('.hero-purchase__checkout, .gdab__confirm');
          if (button && button.textContent !== label) button.textContent = label;
          const chip = surface.querySelector('.gdab__pill .gdab__chip-label');
          if (chip && chip.textContent !== label) chip.textContent = label;
        }
      }
    }
    surface.querySelector('nex-price-compare')?.syncCompare?.();
    if (surface.hasAttribute('data-desktop-preorder')) {
      surface.dataset.inventoryState = !available ? 'soldout' : card.dataset.preorder === 'true' ? 'preorder' : 'stock';
      surface.dataset.inventoryAvailable = String(available);
    }
    if (surface.localName === 'nex-hero-purchase') {
      const caption = surface.closest('.hero')?.querySelector('[data-desktop-preorder]');
      if (caption) {
        caption.dataset.variantId = card.dataset.variantId;
        const mode = caption.querySelector('[data-desktop-preorder-mode]');
        if (mode) mode.textContent = available ? card.dataset.mode || '' : surface.querySelector('.inventory-status__sold-out')?.textContent || '';
        const from = surface.querySelector('[data-preorder-details]');
        const to = caption.querySelector('[data-preorder-details]');
        copyData(from, to);
        const fromLabel = from?.querySelector('[data-gift-progress-label]');
        const toLabel = to?.querySelector('[data-gift-progress-label]');
        if (fromLabel && toLabel) {
          toLabel.textContent = fromLabel.textContent;
          if (fromLabel.dataset.giftLabelTemplate) toLabel.dataset.giftLabelTemplate = fromLabel.dataset.giftLabelTemplate;
        }
        sync(caption, card);
      }
    }
  };
  window.NexPreorderPerks = { sync, stateFor };
  const mount = () => {
    surfaces().forEach(surface => {
      sync(surface);
      surface.querySelectorAll('[data-gift-progress]').forEach(node => {
        if (progressObserver) progressObserver.observe(node);
        else revealProgress(node);
      });
      if (surface.dataset.perksMounted) return;
      surface.dataset.perksMounted = 'true';
      new MutationObserver(records => {
        if (records.some(record => record.target.matches('.hero-purchase__color, .gdab__card'))) sync(surface);
        if (surface.dataset.gdabState === 'panel') surface.querySelectorAll('[data-gift-progress]').forEach(revealProgress);
      }).observe(surface, { subtree: true, attributes: true, attributeFilter: ['aria-checked', 'data-preorder', 'data-preorder-future', 'data-inventory-available', 'data-gdab-state'] });
    });
    document.querySelectorAll('[data-desktop-preorder] [data-gift-progress]').forEach(node => {
      if (progressObserver) progressObserver.observe(node);
      else revealProgress(node);
    });
  };
  document.addEventListener('click', event => {
    const link = event.target.closest('[data-dock-explore]');
    if (!link) return;
    const target = document.getElementById('m1-dock');
    if (!target || target.hidden) return;
    event.preventDefault();
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const heading = target.querySelector('h2');
    if (heading) { heading.tabIndex = -1; heading.focus({ preventScroll: true }); }
    const top = Math.max(0, target.getBoundingClientRect().top + scrollY - 82);
    window.scrollTo({ top, behavior: reduced ? 'instant' : 'smooth' });
  });
  document.addEventListener('nex:inventory-document', event => {
    const fresh = event.detail?.document;
    if (!fresh || window.NexPurchaseReview?.active) return;
    surfaces().forEach(surface => {
      const source = fresh.querySelector(surface.localName);
      if (!source) return;
      surface.querySelectorAll('.hero-purchase__color, .gdab__card').forEach(card => {
        const next = [...source.querySelectorAll('[data-variant-id]')].find(node => node.dataset.variantId === card.dataset.variantId && 'preorderFuture' in node.dataset);
        if (next && card.dataset.preorderFuture !== next.dataset.preorderFuture) card.dataset.preorderFuture = next.dataset.preorderFuture;
      });
      const module = surface.querySelector('[data-preorder-details]');
      const next = source.querySelector('[data-preorder-details]');
      copyData(next, module);
      const label = module?.querySelector('[data-gift-progress-label]');
      const nextLabel = next?.querySelector('[data-gift-progress-label]');
      if (label && nextLabel) {
        label.textContent = nextLabel.textContent;
        delete label.dataset.giftLabelTemplate;
      }
      sync(surface);
    });
  });
  document.addEventListener('nex:dock-config-updated', event => {
    const config = event.detail?.config;
    if (!config || window.NexPurchaseReview?.active) return;
    const pool = Math.max(0, number(config.giftPool));
    const remaining = Math.max(0, number(config.inventoryQuantity));
    const percent = pool > 0 ? Math.floor(Math.max(0, Math.min(100, (pool - remaining) / pool * 100))) : 0;
    surfaces().forEach(surface => {
      surface.querySelectorAll('.hero-purchase__color, .gdab__card').forEach(card => {
        const bag = (config.bags || []).find(bag => String(bag.id) === card.dataset.variantId);
        if (!bag) return;
        setData(card, 'inventoryAvailable', bag.available);
        setData(card, 'preorder', bag.preorder);
        setData(card, 'preorderFuture', bag.future);
        if (bag.inventoryQuantity !== undefined) setData(card, 'inventoryQuantity', bag.inventoryQuantity);
        if (bag.inventoryManaged !== undefined) setData(card, 'inventoryManaged', bag.inventoryManaged);
      });
      surface.querySelectorAll('[data-preorder-details]').forEach(module => {
        setData(module, 'dockAvailable', config.available);
        if (config.inventoryManaged !== undefined) setData(module, 'dockTracked', config.inventoryManaged);
        setData(module, 'giftReady', config.giftOfferEnabled);
        setData(module, 'giftRemaining', remaining);
        setData(module, 'giftPool', pool);
        setData(module, 'giftPercent', percent);
      });
      sync(surface);
    });
  });
  document.addEventListener('shopify:section:load', mount);
  mount();
})();
